import {useState} from 'react';
import { useMutation } from '@tanstack/react-query';
import { lookupPayoutRecipient, payThroughPochiLaBiashara } from '../../../../services/AccountService';
import {toast} from 'react-toastify';
import {formatKenyanPhoneNumber} from "../../../../utils/helpers";
import PayoutRecipientConfirmationModal from './PayoutRecipientConfirmationModal';
import ExpenseTagsPicker from './ExpenseTagsPicker';
import { readErrorEnvelope } from '../../../../utils/errorEnvelope';
import PinInput from '../../PinInput';

const PochiLaBiasharaForm = ({
                               expenseTags,
                               expenseTagsLoading,
                               userWalletBalance,
                               userId,
                               userWalletBalanceLoading,
                               onTransactionSubmitted,
                             }) => {

  const [selectedTags, setSelectedTags] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pinAttemptsRemaining, setPinAttemptsRemaining] = useState(null);
  const [pinLockRetryAfterSeconds, setPinLockRetryAfterSeconds] = useState(null);
  const [formData, setFormData] = useState({
    pochiPhoneNumber: '',
    pochiAmount: '',
    transactionPin: '',
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [verifiedRecipientName, setVerifiedRecipientName] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);

  const payThroughPochiLaBiasharaMutation = useMutation({
    mutationFn: (payload) => payThroughPochiLaBiashara(userId, payload),
  });

  const handleInputChange = (e) => {
    const {id, value} = e.target;
    let finalValue;

    if (id === "pochiPhoneNumber") {
      finalValue = formatKenyanPhoneNumber(value);
    } else {
      finalValue = value;
    }
    setFormData((prev) => ({...prev, [id]: finalValue}));
  };

  const handlePayViaPochiLaBiashara = async (e) => {
    e.preventDefault();

    if (paymentProcessing) return;
    setPaymentProcessing(true);

    const selectedTagIds = (expenseTags || [])
      .filter(tag => selectedTags.includes(tag.name))
      .map(tag => tag.id);

    const payload = {
      PartyB: formData.pochiPhoneNumber,
      Amount: formData.pochiAmount,
      expense_tag_ids: selectedTagIds,
      transaction_pin: formData.transactionPin,
    };

    setPendingPayload(payload);

    try {
      const response = await lookupPayoutRecipient(userId, 'pochi-la-biashara', formData.pochiPhoneNumber);
      setVerifiedRecipientName(response.data.receiver_name);
    } catch {
      setVerifiedRecipientName(null);
    }

    setShowConfirmationModal(true);
  };

  const handleModalCancel = () => {
    setShowConfirmationModal(false);
    setPaymentProcessing(false);
    setPendingPayload(null);
    setVerifiedRecipientName(null);
  };

  const handleModalConfirm = async () => {
    try {
      await toast.promise(
        payThroughPochiLaBiasharaMutation.mutateAsync(pendingPayload),
        {
          pending: 'Processing request ...',
          success: 'Your request will be processed shortly.',
          error: {
            render({ data: err }) {
              return readErrorEnvelope(err).message;
            }
          }
        }
      );
      onTransactionSubmitted();
      resetForm();
    } catch (err) {
      const { errorCode, meta } = readErrorEnvelope(err);
      if (errorCode === 'PIN_INVALID') {
        setPinAttemptsRemaining(meta.remaining_attempts ?? null);
        setPinLockRetryAfterSeconds(null);
      } else if (errorCode === 'PIN_LOCKED') {
        setPinLockRetryAfterSeconds(meta.retry_after_seconds ?? null);
        setPinAttemptsRemaining(null);
      }
    } finally {
      setShowConfirmationModal(false);
      setPaymentProcessing(false);
      setPendingPayload(null);
      setVerifiedRecipientName(null);
    }
  };

  const resetForm = () => {
    setFormData({
      pochiPhoneNumber: '',
      pochiAmount: '',
      transactionPin: '',
    });
    setSelectedTags([]);
    setPinAttemptsRemaining(null);
    setPinLockRetryAfterSeconds(null);
  }

  const isAmountBelowMinimum = formData.pochiAmount !== '' && Number(formData.pochiAmount) < 5;

  const areAllFieldsFilled = () => {
    return formData.pochiPhoneNumber
      && formData.pochiAmount
      && Number(formData.pochiAmount) >= 5
      && selectedTags.length > 0;
  }

  return (
    <>
      <form onSubmit={handlePayViaPochiLaBiashara}>
        <div className="sell-blance">
          <div className="form-label blance"><span>BALANCE:</span>
            {userWalletBalanceLoading ? (
              <span className="spinner-border spinner-border-sm text-primary ms-2" role="status"/>
            ) : (
              <p>{userWalletBalance}</p>
            )}
          </div>
          <label className="form-label text-primary" htmlFor='pochiPhoneNumber'>Pochi Phone Number</label>

          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder=""
              id='pochiPhoneNumber'
              value={formData.pochiPhoneNumber}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="sell-blance">
          <label className="form-label text-primary" htmlFor='pochiAmount'>Amount</label>
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              placeholder=""
              id='pochiAmount'
              min="5"
              step="1"
              value={formData.pochiAmount}
              onChange={handleInputChange}
            />
          </div>
          {isAmountBelowMinimum && (
            <small className="text-danger mt-1 d-block">Minimum amount is KES 5.</small>
          )}
        </div>
        <div className="sell-blance">
          {expenseTagsLoading ? (
            <>
              <label className="form-label text-muted small">Expense Tag (select many):</label>
              <span className="spinner-border spinner-border-sm text-primary ms-2" role="status"/>
            </>
          ) : (
            <ExpenseTagsPicker
              tags={expenseTags}
              selectedNames={selectedTags}
              onChange={setSelectedTags}
            />
          )}
        </div>

        {areAllFieldsFilled() &&
          (
            <div className="sell-blance mt-3">
              <label className="form-label text-danger" htmlFor="transactionPin">
                Enter Transaction PIN
              </label>
              <div className="input-group">
                <PinInput
                  placeholder="Enter your PIN"
                  id="transactionPin"
                  name="transactionPin"
                  value={formData.transactionPin}
                  onChange={handleInputChange}
                />
              </div>
              {pinAttemptsRemaining !== null && (
                <small className={`mt-1 d-block ${pinAttemptsRemaining <= 1 ? 'text-danger' : 'text-warning'}`}>
                  {pinAttemptsRemaining === 0
                    ? 'No attempts remaining.'
                    : `${pinAttemptsRemaining} attempt${pinAttemptsRemaining === 1 ? '' : 's'} remaining.`}
                </small>
              )}
              {pinLockRetryAfterSeconds !== null && (
                <small className="mt-1 d-block text-danger">
                  Your PIN is locked. Try again in {Math.ceil(pinLockRetryAfterSeconds / 60)} minute{Math.ceil(pinLockRetryAfterSeconds / 60) === 1 ? '' : 's'}.
                </small>
              )}
            </div>
          )}
        <div className="text-center">
          <button
            className="btn btn-primary w-75"
            disabled={
              !formData.pochiPhoneNumber ||
              !formData.pochiAmount ||
              Number(formData.pochiAmount) < 5 ||
              selectedTags.length === 0 ||
              !formData.transactionPin ||
              paymentProcessing
            }
          >
            {paymentProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing
              </>
            ) : 'Pay'}
          </button>
        </div>

      </form>

      <PayoutRecipientConfirmationModal
        isOpen={showConfirmationModal}
        recipientName={verifiedRecipientName}
        destination={formData.pochiPhoneNumber}
        amount={formData.pochiAmount}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
    </>
  )
}
export default PochiLaBiasharaForm;
